const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const GRAPH = 'https://graph.microsoft.com/v1.0';

let cachedSiteId = null;
const cachedListIds = {};

async function resolveSiteId(token) {
  if (cachedSiteId) return cachedSiteId;
  const res = await fetch(`${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to resolve SharePoint site (${res.status}): ${await res.text()}`);
  const site = await res.json();
  cachedSiteId = site.id;
  return cachedSiteId;
}

const LIST_DEFINITIONS = {
  PostItBoard: {
    displayName: 'PostItBoard',
    columns: [
      { name: 'EventLink', text: {} },
    ],
  },
  GroceryList: {
    displayName: 'GroceryList',
    columns: [
      { name: 'Priority', text: {} },
      { name: 'PhotoUrl', text: {} },
    ],
  },
  SwimmingTemplates: {
    displayName: 'SwimmingTemplates',
    columns: [
      { name: 'Description', text: { allowMultipleLines: true } },
      { name: 'Sets', text: { allowMultipleLines: true } },
      { name: 'TotalDistance', number: {} },
      { name: 'CreatedByEmail', text: {} },
    ],
  },
  SwimmingWorkouts: {
    displayName: 'SwimmingWorkouts',
    columns: [
      { name: 'TemplateName', text: {} },
      { name: 'Sets', text: { allowMultipleLines: true } },
      { name: 'TotalDistanceMeters', number: {} },
      { name: 'PoolLength', number: {} },
      { name: 'PoolUnit', text: {} },
      { name: 'DurationSeconds', number: {} },
      { name: 'WorkoutDate', text: {} },
      { name: 'CreatedByEmail', text: {} },
      { name: 'WorkoutStartTime', text: {} },
      { name: 'WorkoutEndTime', text: {} },
      // Wall-clock session seconds, written only by History's post-hoc editor
      // (workoutEdit.js). Equal by construction to DurationSeconds and to
      // WorkoutEndTime − WorkoutStartTime on any row that has it; absent on
      // rows never edited, and computeMoving falls through to the stamps.
      { name: 'ActiveSeconds', number: {} },
    ],
  },
  FitnessWeighIns: {
    displayName: 'FitnessWeighIns',
    columns: [
      { name: 'WeightLbs', number: {} },
      { name: 'WeighDate', text: {} },
      { name: 'CreatedByEmail', text: {} },
    ],
  },
  // LiftingWorkouts / LiftingTemplates — provisioned 2026-08-05 by
  // sharepoint-mcp/scripts/provision_lifting_lists.js (list GUIDs
  // 4c02ff04-49a7-4550-be04-37f2b2f5eba9 / 0ad45762-3dc3-4433-9e6a-8c6bdf7e0e28).
  // TotalVolumeLb is INDEXED on the server; not expressible here. Date columns
  // are TEXT holding ISO instants, matching SwimmingWorkouts — read through
  // localDateKey-style parsing, never fromDateOnly's slice. Mirrors
  // SwimmingWorkouts minus the pool columns; templates mirror
  // SwimmingTemplates minus TotalDistance (a template ships with blank
  // weights, so it has no volume to store).
  LiftingWorkouts: {
    displayName: 'LiftingWorkouts',
    columns: [
      { name: 'TemplateName', text: {} },
      { name: 'Sets', text: { allowMultipleLines: true } },
      { name: 'TotalVolumeLb', number: {} },
      { name: 'DurationSeconds', number: {} },
      { name: 'WorkoutDate', text: {} },
      { name: 'CreatedByEmail', text: {} },
      { name: 'WorkoutStartTime', text: {} },
      { name: 'WorkoutEndTime', text: {} },
    ],
  },
  LiftingTemplates: {
    displayName: 'LiftingTemplates',
    columns: [
      { name: 'Description', text: { allowMultipleLines: true } },
      { name: 'Sets', text: { allowMultipleLines: true } },
      { name: 'CreatedByEmail', text: {} },
    ],
  },
  // WalkingWorkouts — provisioned 2026-08-07 by
  // sharepoint-mcp/scripts/provision_walking_list.js (list GUID
  // a97bdf5c-8750-4c74-af3c-9b8ae031eb50). WorkoutDate is TEXT
  // holding an ISO instant, matching SwimmingWorkouts/LiftingWorkouts — read
  // through localDateKey-style parsing, never fromDateOnly's slice. No Sets
  // column (a walk has no reps) and deliberately no WalkingTemplates list.
  // No calories column — burn is computed display-side (liftingBurn.js
  // convention), never stored.
  WalkingWorkouts: {
    displayName: 'WalkingWorkouts',
    columns: [
      { name: 'WorkoutDate', text: {} },
      { name: 'DistanceMiles', number: {} },
      { name: 'DurationSeconds', number: {} },
      // 'easy' | 'steady' | 'hard' — text, not choice; no fitness list uses
      // choice columns and the write path sends plain strings.
      { name: 'Effort', text: {} },
      { name: 'Notes', text: { allowMultipleLines: true } },
      { name: 'CreatedByEmail', text: {} },
    ],
  },
  UserProfiles: {
    displayName: 'UserProfiles',
    columns: [
      { name: 'UserEmail', text: {} },
      { name: 'Nickname', text: {} },
      { name: 'CreatedDate', text: {} },
      { name: 'BriefingCollapsed', boolean: {} },
      { name: 'ReadingsCollapsed', boolean: {} },
      { name: 'ThemeOverride', text: {} },
      { name: 'DarkMode', boolean: {} },
    ],
  },
  Roles: {
    displayName: 'Roles',
    columns: [
      { name: 'Color', text: {} },
      { name: 'SortOrder', number: {} },
      { name: 'CreatedBy', text: {} },
      { name: 'IsActive', boolean: {} },
    ],
  },
  MissionTemplates: {
    displayName: 'MissionTemplates',
    columns: [
      { name: 'Tasks', text: { allowMultipleLines: true } },
      { name: 'DefaultRole', text: {} },
      { name: 'CreatedBy', text: {} },
      { name: 'CreatedDate', text: {} },
    ],
  },
  EventMissions: {
    displayName: 'EventMissions',
    columns: [
      { name: 'EventId', text: {} },
      { name: 'EventTitle', text: {} },
      { name: 'EventDate', text: {} },
      { name: 'EventLocation', text: {} },
      { name: 'TemplateId', text: {} },
      { name: 'TaskStatus', text: { allowMultipleLines: true } },
      { name: 'AdditionalTasks', text: { allowMultipleLines: true } },
      { name: 'PostItLinks', text: { allowMultipleLines: true } },
      { name: 'RoleTag', text: {} },
      { name: 'CreatedBy', text: {} },
    ],
  },
  CoreMemory: {
    displayName: 'CoreMemory',
    columns: [
      { name: 'Fact', text: { allowMultipleLines: true } },
      { name: 'CreatedDate', text: {} },
      { name: 'LastUsed', text: {} },
      { name: 'TimesUsed', number: {} },
      { name: 'PromotedFrom', text: {} },
      { name: 'FlaggedForReview', boolean: {} },
      { name: 'CreatedBy', text: {} },
    ],
  },
  WorkingMemory: {
    displayName: 'WorkingMemory',
    columns: [
      { name: 'Fact', text: { allowMultipleLines: true } },
      { name: 'CreatedDate', text: {} },
      { name: 'LastUsed', text: {} },
      { name: 'TimesUsed', number: {} },
      { name: 'ExpirationDate', text: {} },
      { name: 'RelevanceScore', number: {} },
      { name: 'CreatedBy', text: {} },
    ],
  },
  Procedures: {
    displayName: 'Procedures',
    columns: [
      { name: 'Category', text: {} },
      { name: 'Description', text: { allowMultipleLines: true } },
      { name: 'Steps', text: { allowMultipleLines: true } },
      { name: 'SourceDoc', text: {} },
      { name: 'ConversionStatus', text: {} },
      { name: 'ConversionProgress', number: {} },
      { name: 'CreatedBy', text: {} },
      { name: 'CreatedDate', text: {} },
      { name: 'LastModified', text: {} },
      { name: 'RoleTag', text: {} },
    ],
  },
  ProcedureChangeLog: {
    displayName: 'ProcedureChangeLog',
    columns: [
      { name: 'ProcedureId', text: {} },
      { name: 'ProcedureName', text: {} },
      { name: 'ChangeType', text: {} },
      { name: 'StepNumber', number: {} },
      { name: 'OldValue', text: { allowMultipleLines: true } },
      { name: 'NewValue', text: { allowMultipleLines: true } },
      { name: 'ChangedBy', text: {} },
      { name: 'ChangedDate', text: {} },
    ],
  },
  SortieRuns: {
    displayName: 'SortieRuns',
    columns: [
      { name: 'ProcedureName', text: {} },
      { name: 'ProcedureId', text: {} },
      { name: 'StartDate', text: {} },
      { name: 'CompletedDate', text: {} },
      { name: 'Status', text: {} },
      { name: 'RunBy', text: {} },
      { name: 'StepProgress', text: { allowMultipleLines: true } },
    ],
  },
  KitchenInventory: {
    displayName: 'KitchenInventory',
    columns: [
      { name: 'ItemType', text: {} },
      { name: 'Quantity', number: {} },
      { name: 'LastUpdated', text: {} },
    ],
  },
  PurchaseLog: {
    displayName: 'PurchaseLog',
    columns: [
      { name: 'PurchaseDate', text: {} },
      { name: 'ItemType', text: {} },
      { name: 'Quantity', number: {} },
      { name: 'Cost', number: {} },
      { name: 'Notes', text: {} },
    ],
  },
  DinnerOrders: {
    displayName: 'DinnerOrders',
    columns: [
      { name: 'OrderDate', text: {} },
      { name: 'Headcount', number: {} },
      { name: 'GuestNames', text: {} },
      { name: 'DinnerTime', text: {} },
      { name: 'Protein', text: {} },
    ],
  },
  BriefingCache: {
    displayName: 'BriefingCache',
    columns: [
      { name: 'BriefingText', text: { allowMultipleLines: true } },
      { name: 'ReadingsText', text: { allowMultipleLines: true } },
      { name: 'BriefingDate', text: {} },
      { name: 'UserId', text: {} },
    ],
  },
};

const syncedLists = {};

async function ensureMissingColumns(token, siteId, listId, listName) {
  if (syncedLists[listName]) return;
  syncedLists[listName] = true;
  const def = LIST_DEFINITIONS[listName];
  if (!def || !def.columns || def.columns.length === 0) return;
  try {
    const colsRes = await fetch(
      `${GRAPH}/sites/${siteId}/lists/${listId}/columns?$select=name,displayName&$top=200`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!colsRes.ok) return;
    const colsData = await colsRes.json();
    const existingNames = new Set((colsData.value || []).map(c => (c.name || c.displayName || '').toLowerCase()));
    for (const col of def.columns) {
      if (!existingNames.has(col.name.toLowerCase())) {
        const colBody = { name: col.name, displayName: col.name };
        if (col.text) colBody.text = col.text;
        if (col.number) colBody.number = col.number;
        if (col.boolean) colBody.boolean = col.boolean;
        await fetch(`${GRAPH}/sites/${siteId}/lists/${listId}/columns`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(colBody),
        });
      }
    }
  } catch { /* column sync should never block */ }
}

async function ensureList(token, listName) {
  if (cachedListIds[listName]) return cachedListIds[listName];

  const siteId = await resolveSiteId(token);

  // Try to get the list directly by displayName first (handles lists beyond $top limit)
  const directRes = await fetch(
    `${GRAPH}/sites/${siteId}/lists?$filter=displayName eq '${listName}'&$select=displayName,id`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (directRes.ok) {
    const directData = await directRes.json();
    const match = (directData.value || []).find(
      l => l.displayName.toLowerCase() === listName.toLowerCase()
    );
    if (match) {
      cachedListIds[listName] = match.id;
      await ensureMissingColumns(token, siteId, match.id, listName);
      return match.id;
    }
  }

  // Fallback: scan all lists
  const listsRes = await fetch(
    `${GRAPH}/sites/${siteId}/lists?$select=displayName,id&$top=200`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (listsRes.ok) {
    const listsData = await listsRes.json();
    const existing = (listsData.value || []).find(
      l => l.displayName.toLowerCase() === listName.toLowerCase()
    );
    if (existing) {
      cachedListIds[listName] = existing.id;
      await ensureMissingColumns(token, siteId, existing.id, listName);
      return existing.id;
    }
  }

  // List not found — create it
  const def = LIST_DEFINITIONS[listName];
  if (!def) throw new Error(`No list definition for "${listName}"`);

  const createRes = await fetch(`${GRAPH}/sites/${siteId}/lists`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      displayName: def.displayName,
      list: { template: 'genericList' },
    }),
  });

  // 409 = list already exists (race condition or filter missed it) — look it up again
  if (createRes.status === 409) {
    const retryRes = await fetch(
      `${GRAPH}/sites/${siteId}/lists?$select=displayName,id&$top=200`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!retryRes.ok) throw new Error(`List "${listName}" exists but failed to resolve it (${retryRes.status})`);
    const retryData = await retryRes.json();
    const found = (retryData.value || []).find(
      l => l.displayName.toLowerCase() === listName.toLowerCase()
    );
    if (found) {
      cachedListIds[listName] = found.id;
      return found.id;
    }
    throw new Error(`List "${listName}" reported as existing but could not be found`);
  }

  if (!createRes.ok) throw new Error(`Failed to create list "${listName}" (${createRes.status}): ${await createRes.text()}`);
  const newList = await createRes.json();
  const listId = newList.id;

  // Add custom columns
  for (const col of def.columns) {
    const colBody = { name: col.name, displayName: col.name };
    if (col.text) colBody.text = col.text;
    if (col.number) colBody.number = col.number;
    if (col.boolean) colBody.boolean = col.boolean;

    const colRes = await fetch(`${GRAPH}/sites/${siteId}/lists/${listId}/columns`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(colBody),
    });
    if (!colRes.ok) {
      console.warn(`Failed to create column "${col.name}" on "${listName}": ${colRes.status}`);
    }
  }

  cachedListIds[listName] = listId;
  return listId;
}

function listBase(siteId, listId) {
  return `${GRAPH}/sites/${siteId}/lists/${listId}`;
}

export async function getItems(token, listName) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);
  const res = await fetch(`${base}/items?$expand=fields&$top=500`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to load ${listName} (${res.status}): ${await res.text()}`);
  const data = await res.json();
  return (data.value || []).map(item => ({ id: item.id, ...item.fields }));
}

export async function createItem(token, listName, fields) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);
  const res = await fetch(`${base}/items`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fields }),
  });
  if (!res.ok) throw new Error(`Failed to create item in ${listName} (${res.status}): ${await res.text()}`);
  const created = await res.json();
  return { id: created.id, ...created.fields };
}

export async function updateItem(token, listName, itemId, fields) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);
  const res = await fetch(`${base}/items/${itemId}/fields`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(fields),
  });
  if (!res.ok) throw new Error(`Failed to update item in ${listName} (${res.status}): ${await res.text()}`);
  return res.json();
}

export async function deleteItem(token, listName, itemId) {
  const siteId = await resolveSiteId(token);
  const listId = await ensureList(token, listName);
  const base = listBase(siteId, listId);
  const res = await fetch(`${base}/items/${itemId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to delete item in ${listName} (${res.status}): ${await res.text()}`);
}

export const YARDS_TO_METERS = 0.9144;

export function convertToMeters(distance, unit) {
  return unit === 'yd' ? distance * YARDS_TO_METERS : distance;
}

export function convertFromMeters(meters, unit) {
  return unit === 'yd' ? meters / YARDS_TO_METERS : meters;
}

export const STROKES = ['Free', 'Back', 'Breast', 'Fly', 'IM', 'Kick', 'Pull', 'Drill', 'Choice'];
